import axios from "axios";
import {
  preserveTrackingKeysDuringSessionClear,
  restoreTrackingKeysAfterSessionClear,
} from "../utils/affiliateTracking";
import { handleExpiredSession } from "../utils/expiredSession";

export const initAxiosAuth = () => {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      // Handle case where error.response doesn't exist
      if (!error.response) {
        return Promise.reject(error);
      }

      const { status } = error.response;

      if (status === 404) {
        console.log("not found");
      }

      if (status === 401) {
        const hadSession = localStorage.getItem("session") !== null;
        if (hadSession) {
          const trackingKeys = preserveTrackingKeysDuringSessionClear();
          localStorage.clear();
          restoreTrackingKeysAfterSessionClear(trackingKeys);
          handleExpiredSession();
        }
      }

      if (status === 403) {
        console.log("forbidden", error);
        // Don't reject for 403 - let it be returned so createComment can handle it
        return error.response;
      }

      // Return error response for ALL error statuses (4xx, 5xx, etc.)
      // This allows components/utilities to handle them appropriately
      return Promise.reject(error.response);
    }
  );

  axios.interceptors.request.use(
    (config) => {
      config.headers["Content-Type"] = "application/json";
      return config;
    },
    (error) => Promise.reject(error)
  );
};

const getToken = () => {
  if (localStorage.getItem("session") !== null) {
    return JSON.parse(localStorage.getItem("session"));
  } else {
    return null;
  }
};

export const registerUser = async (data) => {
  return axios
    .put(process.env.REACT_APP_API_URL + "user/register", data, {
      headers: {},
    })
    .then((response) => {
      return Promise.resolve(response);
    })
    .catch((error) => {
      console.log("error", error);
      return Promise.resolve(error);
    });
};

export const pageVisit = async () => {
  const token = getToken();
  const headers = {};
  if (token !== null) {
    headers["X-Auth-Token"] = token;

    return axios
      .put(
        `${process.env.REACT_APP_API_URL}user/page-visited`,
        {},
        {
          headers: headers,
        }
      )
      .then((response) => {
        return Promise.resolve(response);
      })
      .catch((error) => {
        console.log("error", error);
        return Promise.resolve(error);
      });
  }
};

export const updateUserSettings = async (data) => {
  const token = getToken();
  const headers = {};
  if (token !== null) {
    headers["X-Auth-Token"] = token;
  }
  return axios
    .put(`${process.env.REACT_APP_API_URL}user/settings`, data, {
      headers: headers,
    })
    .then((response) => {
      return Promise.resolve(response);
    })
    .catch((error) => {
      console.log("error", error);
      return Promise.resolve(error);
    });
};

export const loginUser = async (data) => {
  return axios
    .post(process.env.REACT_APP_API_URL + "user/login", data, {
      headers: {},
    })
    .then((response) => {
      return Promise.resolve(response);
    })
    .catch((error) => {
      return Promise.resolve(error);
    });
};

export const changeWallet = async (data) => {
  return axios
    .post(
      process.env.REACT_APP_API_URL + "subscription/acquire-lifetime-with-nft",
      data,
      {
        headers: {
          "X-Auth-Token": getToken(),
        },
      }
    )
    .then((response) => {
      return Promise.resolve(response);
    })
    .catch((error) => {
      return Promise.resolve(error);
    });
};

export const Postcancelplan = async (subscriptionId) => {
  return axios
    .patch(
      `${process.env.REACT_APP_API_URL}subscription/cancel/${subscriptionId}`,
      {},
      {
        headers: {
          "X-Auth-Token": getToken(),
          "Content-Type": "application/json",
        },
      }
    )
    .then((response) => {
      return Promise.resolve(response);
    })
    .catch((error) => {
      return Promise.resolve(error);
    });
};

export const validateCoupon = async (data) => {
  return axios
    .post(process.env.REACT_APP_API_URL + "coupon/check-applicability", data, {
      headers: {},
    })
    .then((response) => {
      return Promise.resolve(response);
    })
    .catch((error) => {
      return Promise.resolve(error);
    });
};

export const getUser = async (userId) => {
  const token = getToken();
  const headers = {};
  if (token !== null) {
    headers["X-Auth-Token"] = token;
  }
  return axios.get(`${process.env.REACT_APP_API_URL}user/${userId}`, {
    data: {},
    headers: headers,
  });
};

export const getPublishers = async () => {
  return axios.get(process.env.REACT_APP_API_URL + "publisher/list/all", {
    data: {},
    headers: {
      "Content-Type": "application/json",
    },
  });
};

export const getPublisherVote = async (userId) => {
  const token = getToken();
  const headers = {};
  if (token !== null) {
    headers["X-Auth-Token"] = token;
  }
  return axios.get(
    `${process.env.REACT_APP_API_URL}publisher-vote/${userId}/all`,
    {
      data: {},
      headers: headers,
    }
  );
};

export const getNotifications = async (userId) => {
  const token = getToken();
  const headers = {};
  if (token !== null) {
    headers["X-Auth-Token"] = token;
  }
  return axios.get(
    `${process.env.REACT_APP_API_URL}platform-notification/all`,
    {
      data: {},
      headers: headers,
    }
  );
};

export const NotificationsRead = async (userId) => {
  const token = getToken();
  const headers = {};
  if (token !== null) {
    headers["X-Auth-Token"] = token;
  }
  return axios.patch(
    `${process.env.REACT_APP_API_URL}platform-notification/consume-all`,
    {},
    {
      headers: {
        "X-Auth-Token": getToken(),
        "Content-Type": "application/json",
      },
    }
  );
};

export const setPublisherVote = async (userId, data) => {
  try {
    return axios.put(
      `${process.env.REACT_APP_API_URL}publisher-vote/${userId}/all`,
      data,
      {
        headers: {
          "X-Auth-Token": getToken(),
        },
      }
    );
  } catch (error) {
    return error;
  }
};

export const patchUser = async (userId, data) => {
  const token = getToken();
  const headers = {};
  if (token !== null) {
    headers["X-Auth-Token"] = token;
  }
  return axios
    .patch(`${process.env.REACT_APP_API_URL}user/${userId}`, data, {
      headers: headers,
    })
    .then((response) => {
      return Promise.resolve(response);
    })
    .catch((error) => {
      console.log("error", error);
      return Promise.resolve(error);
    });
};

export const uploadAvatar = async (userId, data) => {
  return axios
    .put(`${process.env.REACT_APP_API_URL}user/avatar/${userId}`, data, {
      headers: {
        "X-Auth-Token": getToken(),
      },
    })
    .then((response) => {
      return Promise.resolve(response);
    })
    .catch((error) => {
      console.log("error", error);
      return Promise.resolve(error);
    });
};

export const getPaymentAllCards = async (user) => {
  const token = getToken();
  const headers = {};
  if (token !== null) {
    headers["X-Auth-Token"] = token;
  }
  return axios
    .get(`${process.env.REACT_APP_API_URL}payment/${user}/card/all`, {
      data: {},
      headers: headers,
    })
    .then((response) => {
      return Promise.resolve(response);
    })
    .catch((error) => {
      console.log("error", error);
      return Promise.resolve(error);
    });
};

export const getNFTCollection = async (user) => {
  const headers = {};
  return axios
    .get(`${process.env.REACT_APP_API_URL}payment/nft/collection/list/all`, {
      data: {},
      headers: headers,
    })
    .then((response) => {
      return Promise.resolve(response);
    })
    .catch((error) => {
      console.log("error", error);
      return Promise.resolve(error);
    });
};

export const getSubscriptionStatus = async (user) => {
  const token = getToken();
  const headers = {};
  if (token !== null) {
    headers["X-Auth-Token"] = token;
  }
  return axios.get(
    `${process.env.REACT_APP_API_URL}subscription/latest-by-product-for-user/${user}`,
    {
      data: {},
      headers: headers,
    }
  );
};

export const resetPassword = async (email) => {
  const headers = {};
  return axios
    .get(
      `${process.env.REACT_APP_API_URL}user/reset-password-request/${email}`,
      {
        data: {},
        headers: headers,
      }
    )
    .then((response) => {
      return Promise.resolve(response);
    })
    .catch((error) => {
      console.log("error", error);
      return Promise.resolve(error);
    });
};

export const getInvoiceState = async (invoiceId) => {
  const headers = {};
  return axios
    .get(`${process.env.REACT_APP_API_URL}payment/status/${invoiceId}`, {
      data: {},
      headers: headers,
    })
    .then((response) => {
      return Promise.resolve(response);
    })
    .catch((error) => {
      console.log("error", error);
      return Promise.resolve(error);
    });
};

export const getPaymentHistory = async (user, productId) => {
  const token = getToken();
  const headers = {};
  if (token !== null) {
    headers["X-Auth-Token"] = token;
  }
  return axios.get(
    `${process.env.REACT_APP_API_URL}payment/history/${user}/*`,
    {
      data: {},
      headers: headers,
    }
  );
};

export const deletePaymentCard = async (userId, cardId) => {
  const token = getToken();
  const headers = {};
  if (token !== null) {
    headers["X-Auth-Token"] = token;
  }
  return axios.delete(
    `${process.env.REACT_APP_API_URL}payment/${userId}/card/${cardId}`,
    {
      data: {},
      headers: headers,
    }
  );
};

export const createPaymentCard = async (userId, data) => {
  return axios
    .put(`${process.env.REACT_APP_API_URL}payment/${userId}/card`, data, {
      headers: {
        "X-Auth-Token": getToken(),
      },
    })
    .then((response) => {
      return Promise.resolve(response);
    })
    .catch((error) => {
      return Promise.resolve(error);
    });
};

export const getAllEnabledProducts = async (data) => {
  return axios.get(
    process.env.REACT_APP_API_URL + "product/list/for-registration",
    {
      data: {},
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
};

export const getAllEnabledProductsBilling = async (data) => {
  const token = getToken();
  const headers = {};
  if (token !== null) {
    headers["X-Auth-Token"] = token;
  }
  return axios.get(
    process.env.REACT_APP_API_URL + "product/list/for-billing-page",
    {
      data: {},
      headers: headers,
    }
  );
};
export const createProduct = async () => {
  const token = getToken();
  const headers = {};
  if (token !== null) {
    headers["X-Auth-Token"] = token;
  }
  const data = {
    name: "Bitcoin Live Monthly",
    cost: 149,
    billingCycle: 1,
    link: "/test-product",
    description:
      "Try us for one month. Full premium access to all content. If you love it, just keep the plan. Or you can upgrade to our quarterly option and pay just $99 per month!",
  };
  try {
    return axios.put(process.env.REACT_APP_API_URL + "product", data, {
      headers: headers,
    });
  } catch (error) {
    return error;
  }
};

export const getAllEductionTopics = async () => {
  return axios.get(
    process.env.REACT_APP_API_URL + "post/education/all-topics",
    {
      data: {},
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
};

export const getProduct = async (productId) => {
  return axios.get(`${process.env.REACT_APP_API_URL}product/${productId}`, {
    data: {},
    headers: {
      "Content-Type": "application/json",
    },
  });
};

export const getMixedPostsForPublisher = async (publisherId, size, page) => {
  const token = getToken();
  const headers = {};
  if (token !== null) {
    headers["X-Auth-Token"] = token;
  }
  return axios.get(
    `${process.env.REACT_APP_API_URL}post/mixed/${publisherId}/${size}/${page}`,
    {
      data: {},
      headers: headers,
    }
  );
};

export const getPublicPostsForPublisher = async (publisherId, size, page) => {
  const token = getToken();
  const headers = {};
  if (token !== null) {
    headers["X-Auth-Token"] = token;
  }
  return axios.get(
    `${process.env.REACT_APP_API_URL}post/public/${publisherId}/${size}/${page}`,
    {
      data: {},
      headers: headers,
    }
  );
};

export const getMemberPosts = async (publisherId, size, page) => {
  const token = getToken();
  const headers = {};
  if (token !== null) {
    headers["X-Auth-Token"] = token;
  }
  return axios.get(
    `${process.env.REACT_APP_API_URL}post/member/${publisherId}/${size}/${page}`,
    {
      data: {},
      headers: headers,
    }
  );
};

export const getFovuritePosts = async () => {
  const token = getToken();
  const headers = {};
  if (token !== null) {
    headers["X-Auth-Token"] = token;
  }
  return axios.get(`${process.env.REACT_APP_API_URL}favourites/posts`, {
    data: {},
    headers: headers,
  });
};

export const getFavoriteAlerts = async () => {
  const token = getToken();
  const headers = {};
  if (token !== null) {
    headers["X-Auth-Token"] = token;
  }
  return axios.get(`${process.env.REACT_APP_API_URL}favourites/alerts`, {
    data: {},
    headers: headers,
  });
};

export const getPublicPosts = async (publisherId, size, page) => {
  const token = getToken();
  const headers = {};
  return axios.get(
    `${process.env.REACT_APP_API_URL}post/public/${publisherId}/${size}/${page}`,
    {
      data: {},
      headers: headers,
    }
  );
};

export const getMemberAlerts = async (
  publisherId,
  filter,
  pageSize,
  pageIndex
) => {
  const token = getToken();
  const headers = {};
  if (token !== null) {
    headers["X-Auth-Token"] = token;
  }
  return axios.get(
    `${process.env.REACT_APP_API_URL}alert/list/mixed/${publisherId}/${filter}/${pageSize}/${pageIndex}`,
    {
      data: {},
      headers: headers,
    }
  );
};

export const getAlert = async (alertId) => {
  const token = getToken();
  const headers = {};
  if (token !== null) {
    headers["X-Auth-Token"] = token;
  }
  return axios.get(`${process.env.REACT_APP_API_URL}alert/${alertId}`, {
    data: {},
    headers: headers,
  });
};

export const getMixedAlerts = async (
  publisherId,
  filter,
  pageSize,
  pageIndex
) => {
  const token = getToken();
  const headers = {};
  if (token !== null) {
    headers["X-Auth-Token"] = token;
  }
  return axios.get(
    `${process.env.REACT_APP_API_URL}alert/list/mixed/${publisherId}/${filter}/${pageSize}/${pageIndex}`,
    {
      data: {},
      headers: headers,
    }
  );
};

export const getPublicAlertsForPublisher = async (publisherId, size, page) => {
  const token = getToken();
  const filters = "*";
  const headers = {};
  if (token !== null) {
    headers["X-Auth-Token"] = token;
  }
  return axios.get(
    `${process.env.REACT_APP_API_URL}alert/mixed/${publisherId}/${filters}/${size}/${page}`,
    {
      data: {},
      headers: headers,
    }
  );
};

export const getEducationalPosts = async (filter, size, page) => {
  const token = getToken();
  const headers = {};
  if (token !== null) {
    headers["X-Auth-Token"] = token;
  }
  return axios.post(
    `${process.env.REACT_APP_API_URL}post/search/${size}/${page}`,
    filter,
    {
      headers: headers,
    }
  );
};

export const renewOrBuySubscription = async (userId, data) => {
  return axios
    .put(`${process.env.REACT_APP_API_URL}subscription/buy`, data, {
      headers: {
        "X-Auth-Token": getToken(),
      },
    })
    .then((response) => {
      return Promise.resolve(response);
    })
    .catch((error) => {
      console.log("error", error);
      return Promise.resolve(error);
    });
};

export const renewSubscription = async (subscriptionId, data) => {
  return axios
    .patch(
      `${process.env.REACT_APP_API_URL}subscription/renew/${subscriptionId}`,
      data,
      {
        headers: {
          "X-Auth-Token": getToken(),
        },
      }
    )
    .then((response) => {
      return Promise.resolve(response);
    })
    .catch((error) => {
      console.log("error", error);
      return Promise.resolve(error);
    });
};

export const reEnableSubscription = async (userId, subscriptionId) => {
  return axios
    .patch(
      `${process.env.REACT_APP_API_URL}subscription/re-enable/${subscriptionId}`,
      {},
      {
        headers: {
          "X-Auth-Token": getToken(),
        },
      }
    )
    .then((response) => {
      return Promise.resolve(response);
    })
    .catch((error) => {
      console.log("error", error);
      return Promise.resolve(error);
    });
};

export const changePaymentMode = async (data, subscriptionId) => {
  return axios
    .patch(
      `${process.env.REACT_APP_API_URL}subscription/change-renewal-payment-method/${subscriptionId}`,
      data,
      {
        headers: {
          "X-Auth-Token": getToken(),
        },
      }
    )
    .then((response) => {
      return Promise.resolve(response);
    })
    .catch((error) => {
      console.log("error", error);
      return Promise.resolve(error);
    });
};

export const getNotificationPreference = async (userId) => {
  return axios.get(
    `${process.env.REACT_APP_API_URL}notification-preference/${userId}/all`,
    {
      data: {},
      headers: {
        "X-Auth-Token": getToken(),
      },
    }
  );
};

export const setNotificationPreference = async (userId, data) => {
  try {
    return axios.put(
      `${process.env.REACT_APP_API_URL}notification-preference/${userId}/all`,
      data,
      {
        headers: {
          "X-Auth-Token": getToken(),
        },
      }
    );
  } catch (error) {
    return error;
  }
};

export const getSiteConfig = async () => {
  return axios.get(process.env.REACT_APP_API_URL + "config/all", {
    data: {},
    headers: {
      "X-Auth-Token": getToken(),
    },
  });
};

export const getPostShortList = async (size, index, filters) => {
  return axios.post(
    `${process.env.REACT_APP_API_URL}post/short-list/${size}/${index}`,
    {
      filters,
    },
    {
      headers: {
        "X-Auth-Token": getToken(),
      },
    }
  );
};

export const getPost = async (id) => {
  const token = getToken();
  const headers = {};
  if (token !== null) {
    headers["X-Auth-Token"] = token;
  }
  return axios.get(`${process.env.REACT_APP_API_URL}post/${id}`, {
    data: {},
    headers: headers,
  });
};

export const getPostBySlug = async (slug) => {
  const token = getToken();
  const headers = {};
  if (token !== null) {
    headers["X-Auth-Token"] = token;
  }

  return axios.get(`${process.env.REACT_APP_API_URL}post/by-slug/${slug}`, {
    data: {},
    headers: headers,
  });
};

export const getComments = async (postId) => {
  const token = getToken();
  const headers = {};
  if (token !== null) {
    headers["X-Auth-Token"] = token;
  }
  return axios.get(
    `${process.env.REACT_APP_API_URL}comment/for-post/${postId}`,
    {
      data: {},
      headers: headers,
    }
  );
};

export const createComment = async (data) => {
  try {
    return axios.put(`${process.env.REACT_APP_API_URL}comment`, data, {
      headers: {
        "X-Auth-Token": getToken(),
      },
    });
  } catch (error) {
    return error;
  }
};

export const createFavoritePost = async (postId) => {
  try {
    return axios.put(
      `${process.env.REACT_APP_API_URL}favourites/posts/${postId} `,
      {},
      {
        headers: {
          "X-Auth-Token": getToken(),
        },
      }
    );
  } catch (error) {
    return error;
  }
};

export const removeFavoritePost = async (commentId) => {
  try {
    return axios.delete(
      `${process.env.REACT_APP_API_URL}favourites/posts/${commentId}`,
      {
        data: {},
        headers: {
          "X-Auth-Token": getToken(),
        },
      }
    );
  } catch (error) {
    return error;
  }
};

export const setrackingAlert = async (alertId, enabled) => {
  if (enabled) {
    try {
      return axios.put(
        `${process.env.REACT_APP_API_URL}alert/tracking/${alertId} `,
        {},
        {
          headers: {
            "X-Auth-Token": getToken(),
          },
        }
      );
    } catch (error) {
      return error;
    }
  } else {
    try {
      return axios.delete(
        `${process.env.REACT_APP_API_URL}alert/tracking/${alertId} `,
        {
          data: {},
          headers: {
            "X-Auth-Token": getToken(),
          },
        }
      );
    } catch (error) {
      return error;
    }
  }
};

export const createFavoriteAlert = async (alertId) => {
  try {
    return axios.put(
      `${process.env.REACT_APP_API_URL}favourites/alerts/${alertId} `,
      {},
      {
        headers: {
          "X-Auth-Token": getToken(),
        },
      }
    );
  } catch (error) {
    return error;
  }
};

export const removeFavoriteAlert = async (alertId) => {
  try {
    return axios.delete(
      `${process.env.REACT_APP_API_URL}favourites/alerts/${alertId}`,
      {
        data: {},
        headers: {
          "X-Auth-Token": getToken(),
        },
      }
    );
  } catch (error) {
    return error;
  }
};

export const createLike = async (commentId) => {
  try {
    return axios.put(
      `${process.env.REACT_APP_API_URL}comment/like/${commentId}`,
      {},
      {
        headers: {
          "X-Auth-Token": getToken(),
        },
      }
    );
  } catch (error) {
    return error;
  }
};

export const trackComment = async (commentId) => {
  try {
    return axios.put(
      `${process.env.REACT_APP_API_URL}comment/tracking/${commentId}`,
      {},
      {
        headers: {
          "X-Auth-Token": getToken(),
        },
      }
    );
  } catch (error) {
    return error;
  }
};

export const stopTrackingComment = async (commentId) => {
  try {
    return axios.delete(
      `${process.env.REACT_APP_API_URL}comment/tracking/${commentId}`,
      {
        data: {},
        headers: {
          "X-Auth-Token": getToken(),
        },
      }
    );
  } catch (error) {
    return error;
  }
};

export const createPublicAccess = async (
  email,
  name,
  inviterAffiliateKey,
  marketingSourceKey,
  returnUrl
) => {
  return axios
    .put(
      `${process.env.REACT_APP_API_URL}presale/public-access`,
      {
        email,
        name,
        inviterAffiliateKey,
        marketingSourceKey,
        returnUrl,
      },
      {
        headers: {},
      }
    )
    .then((response) => {
      return Promise.resolve(response);
    })
    .catch((error) => {
      console.log("error", error);
      return Promise.resolve(error);
    });
};

export const subscribeToNewsletter = async (
  email,
  name,
  inviterAffiliateKey,
  marketingSourceKey
) => {
  return axios
    .put(
      `${process.env.REACT_APP_API_URL}presale/public-access-without-verification`,
      {
        email,
        name,
        inviterAffiliateKey,
        marketingSourceKey,
      },
      {
        headers: {},
      }
    )
    .then((response) => {
      return Promise.resolve(response);
    })
    .catch((error) => {
      console.log("error", error);
      return Promise.resolve(error);
    });
};

export const getPublicAccessCheck = async (email) => {
  return axios.get(
    `${process.env.REACT_APP_API_URL}presale/public-access/check/${email}`,
    {
      data: {},
      headers: {},
    }
  );
};

export const removeLike = async (commentId) => {
  try {
    return axios.delete(
      `${process.env.REACT_APP_API_URL}comment/like/${commentId}`,
      {
        data: {},
        headers: {
          "X-Auth-Token": getToken(),
        },
      }
    );
  } catch (error) {
    return error;
  }
};

export const removeComment = async (commentId) => {
  return axios.delete(`${process.env.REACT_APP_API_URL}comment/${commentId}`, {
    data: {},
    headers: {
      "X-Auth-Token": getToken(),
    },
  });
};

export const createLikeOnPost = async (postId) => {
  try {
    return axios.put(
      `${process.env.REACT_APP_API_URL}post/like/${postId}`,
      {},
      {
        headers: {
          "X-Auth-Token": getToken(),
        },
      }
    );
  } catch (error) {
    return error;
  }
};

export const deleteLikeOnPost = async (postId) => {
  try {
    return axios.delete(`${process.env.REACT_APP_API_URL}post/like/${postId}`, {
      data: {},
      headers: {
        "X-Auth-Token": getToken(),
      },
    });
  } catch (error) {
    return error;
  }
};

export const getPostTagSearch = async (search) => {
  const token = getToken();
  const headers = {};
  if (token !== null) {
    headers["X-Auth-Token"] = token;
  }
  return axios.get(`${process.env.REACT_APP_API_URL}tag/search/${search}`, {
    data: {},
    headers: headers,
  });
};

export const getTradingUnit = async () => {
  const token = getToken();
  const headers = {};
  if (token !== null) {
    headers["X-Auth-Token"] = token;
  }
  return axios.get(`${process.env.REACT_APP_API_URL}trading-unit/list/all`, {
    data: {},
    headers: headers,
  });
};

export const createAlert = async (data) => {
  return axios
    .put(`${process.env.REACT_APP_API_URL}alert`, data, {
      headers: {
        "X-Auth-Token": getToken(),
      },
    })
    .then((response) => {
      return Promise.resolve(response);
    })
    .catch((error) => {
      console.log("error", error);
      return Promise.resolve(error);
    });
};

export const deletePost = async (postId) => {
  try {
    return axios.delete(`${process.env.REACT_APP_API_URL}post/${postId}`, {
      data: {},
      headers: {
        "X-Auth-Token": getToken(),
      },
    });
  } catch (error) {
    return error;
  }
};

export const createAlertUpdate = async (data) => {
  try {
    return axios.put(process.env.REACT_APP_API_URL + "alert/update", data, {
      headers: {
        "X-Auth-Token": getToken(),
      },
    });
  } catch (error) {
    return error;
  }
};

export const getWistiaConfig = async () => {
  return axios.get(process.env.REACT_APP_API_URL + "config/wistia", {
    data: {},
    headers: {
      "X-Auth-Token": getToken(),
    },
  });
};

export const publishPost = async (data) => {
  try {
    const response = await axios.put(`${process.env.REACT_APP_API_URL}post`, data, {
      headers: {
        "X-Auth-Token": getToken(),
      },
    });
    return response;
  } catch (error) {
    // Return error response with status code intact
    if (error && error.status) {
      return error;
    }
    // If no status, create a response-like object
    return {
      status: 500,
      data: error.response?.data || { error: error.message },
      error: error,
    };
  }
};

export const updatePost = async (postId, data) => {
  try {
    const response = await axios.patch(`${process.env.REACT_APP_API_URL}post/${postId}`, data, {
      headers: {
        "X-Auth-Token": getToken(),
      },
    });
    return response;
  } catch (error) {
    // Return error response with status code intact
    if (error && error.status) {
      return error;
    }
    // If no status, create a response-like object
    return {
      status: 500,
      data: error.response?.data || { error: error.message },
      error: error,
    };
  }
};

export const updateAlert = async (alertId, data) => {
  try {
    return axios.patch(
      `${process.env.REACT_APP_API_URL}alert/${alertId}`,
      data,
      {
        headers: {
          "X-Auth-Token": getToken(),
        },
      }
    );
  } catch (error) {
    return error;
  }
};
